<script setup lang="ts">
/**
 * ColumnVisibilityMenu — dropdown of checkbox toggles for table columns.
 *
 * Used in Batch A's UsersTable to expose the legacy financial
 * columns (tax, minGuarantee, etc.) — hidden by default but
 * available to admins who need them.
 *
 * Two-way bound via v-model:visible — an array of column IDs that
 * are currently visible. The component renders one checkbox per
 * column and updates the array as toggles flip.
 *
 * Locked columns (e.g. the primary key column you never want to
 * hide) are passed as a separate `lockedIds` array — they appear in
 * the menu with a disabled, always-checked state.
 */
import { computed } from 'vue'
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from 'reka-ui'
import { Columns3 } from 'lucide-vue-next'
import Button from './Button.vue'
import Checkbox from './Checkbox.vue'

export interface ColumnDef {
  /** Stable column identifier. Matches the row's accessor key. */
  id: string
  /** Display label shown in the menu. */
  label: string
}

interface Props {
  /** All columns the table supports, in display order. */
  columns: ColumnDef[]
  /** IDs of columns currently visible. */
  visible: string[]
  /** IDs that can't be hidden (e.g. primary key). Optional. */
  lockedIds?: string[]
}

const props = withDefaults(defineProps<Props>(), { lockedIds: () => [] })

const emit = defineEmits<{
  'update:visible': [ids: string[]]
}>()

const lockedSet = computed(() => new Set(props.lockedIds))

function toggle(id: string, checked: boolean): void {
  if (lockedSet.value.has(id)) return
  if (checked) {
    if (!props.visible.includes(id)) {
      emit('update:visible', [...props.visible, id])
    }
  } else {
    emit(
      'update:visible',
      props.visible.filter((v) => v !== id),
    )
  }
}

function isVisible(id: string): boolean {
  if (lockedSet.value.has(id)) return true
  return props.visible.includes(id)
}
</script>

<template>
  <PopoverRoot>
    <PopoverTrigger as-child>
      <Button kind="ghost" size="sm" :icon="Columns3">
        {{ $t('table.columns.label') }}
      </Button>
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent
        :side-offset="6"
        align="end"
        class="w-60 p-1.5 bg-surface border border-border-strong rounded-lg
               shadow-lg z-50 outline-none"
      >
        <p
          class="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-text-4"
        >{{ $t('table.columns.title') }}</p>
        <label
          v-for="col in columns"
          :key="col.id"
          class="flex items-center gap-2 px-2 py-1.5 text-xs text-text-2
                 hover:bg-surface-2 hover:text-text-1 rounded cursor-pointer
                 transition-colors"
        >
          <Checkbox
            :model-value="isVisible(col.id)"
            :disabled="lockedSet.has(col.id)"
            @update:model-value="(v) => toggle(col.id, v === true)"
          />
          <span class="flex-1 truncate">{{ col.label }}</span>
          <span
            v-if="lockedSet.has(col.id)"
            class="text-[9px] text-text-5 font-mono"
          >{{ $t('table.columns.locked') }}</span>
        </label>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>
